import { useGetList, useLocaleState, useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

import type { Project, ProjectScore, ScoreLine } from "../types";
import { useAccountCurrency } from "./CurrencySelector";
import { MethodStamp } from "./MethodStamp";
import { categoryKey, phaseKey } from "./projectUtils";
import { useInvited } from "./useInvited";
import { HelpTip } from "./HelpTip";

/** Le score mêle règles et évaluation de l'IA : il porte toujours le tampon ambre. */
export const ScoreStamp = ({ className }: { className?: string }) => {
  const translate = useTranslate();
  return (
    <MethodStamp
      method="inferred"
      label={translate("resources.projects.score.stamp")}
      title={translate("resources.projects.score.stamp_title")}
      className={className}
    />
  );
};

/** Score produit de la fiche : total, explication, puis le détail critère par critère. */
export const ScoreCard = ({ project }: { project: Project }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const invited = useInvited();
  const { data, isPending } = useGetList<ProjectScore>("scores", {
    filter: { project_id: project.id, product_id: "ett" },
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "ASC" },
  });
  if (isPending) return null;
  const score = data?.[0];
  // Stade tardif, petit montant ou ville inconnue : le total est plafonné sous la somme des critères.
  const uncapped = Math.round(
    (score?.detail ?? []).reduce((sum, line) => sum + line.points, 0),
  );

  return (
    <Card data-anchor="score">
      <CardContent className="flex flex-col gap-3">
        <h3 className="flex items-center gap-1 text-sm font-bold">
          {translate("resources.projects.score.title")}
          <HelpTip topic="score" />
        </h3>
        {!score ? (
          <p className="text-sm text-muted-foreground">
            {translate("resources.projects.score.not_scored")}
          </p>
        ) : (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums">
                {score.score}
              </span>
              <span className="text-sm text-muted-foreground">
                {translate("resources.projects.score.out_of")}
              </span>
              <ScoreStamp className="ml-1 self-center" />
            </div>
            {score.to_specify ? (
              <p className="text-xs text-inferred-ink">
                {translate("resources.projects.score.to_specify", { uncapped })}
              </p>
            ) : (
              score.score < uncapped && (
                <p className="text-xs text-inferred-ink">
                  {translate(
                    project.city
                      ? "resources.projects.score.capped"
                      : "resources.projects.score.capped_no_city",
                    { uncapped },
                  )}
                </p>
              )
            )}
            <p className="text-sm">{score.explanation}</p>
            {/* Le détail est long : replié pour que la fiche reste un relevé, ouvert à la demande. */}
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                {translate("resources.projects.score.details")}
              </summary>
              <ul className="mt-3 flex flex-col gap-3">
                {score.detail.map((line) => (
                  <li key={line.criterion} className="text-sm">
                    <div className="flex justify-between gap-2">
                      <span className="font-medium">
                        {translate(
                          `resources.projects.score.criteria.${line.criterion}`,
                          { _: line.criterion },
                        )}
                        {line.method === "ai" && (
                          <Badge variant="outline" className="ml-2">
                            {translate("resources.projects.score.by_ai")}
                          </Badge>
                        )}
                      </span>
                      <span className="text-muted-foreground whitespace-nowrap">
                        {translate("resources.projects.score.points", {
                          points: line.points.toLocaleString(locale),
                          weight: line.weight,
                        })}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      <LineBasis line={line} />
                    </div>
                  </li>
                ))}
              </ul>
              {score.surface_m2 && score.surface_method === "extracted" && (
                <div className="text-sm">
                  {translate("resources.projects.score.surface", {
                    surface: score.surface_m2.toLocaleString(locale),
                  })}
                  {/* Citation de la source : réservée aux abonnés, comme les sources. */}
                  {!invited && (
                    <div className="text-xs text-muted-foreground italic mt-0.5">
                      « {score.surface_quote} »
                    </div>
                  )}
                </div>
              )}
              {score.product_need_litres && (
                <div className="text-sm">
                  {translate("resources.projects.score.need", {
                    litres: score.product_need_litres.toLocaleString(locale),
                  })}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {translate("resources.projects.score.ai_note", {
                  confidence: Math.round(score.confidence * 100),
                })}{" "}
                {translate("resources.projects.score.provisional")}
              </p>
            </details>
          </>
        )}
      </CardContent>
    </Card>
  );
};

/** Ce qui justifie la ligne : la valeur de la fiche pour une règle, la raison du modèle pour l'IA. */
const LineBasis = ({ line }: { line: ScoreLine }) => {
  const translate = useTranslate();
  const { estimate } = useAccountCurrency();
  if (line.level === null || !line.basis)
    return translate("resources.projects.score.unknown");
  if (line.criterion === "structure_type")
    return translate(categoryKey(line.basis));
  if (line.criterion === "phase")
    return translate(phaseKey(line.basis), { _: line.basis });
  if (line.criterion === "size")
    return translate("resources.projects.score.amount", {
      // Le montant du score est en euros : affiché dans la monnaie du compte.
      amount: estimate(parseFloat(line.basis)).replace(/^≈ /, ""),
    });
  return line.basis;
};
